/**
 * SEO URL Utilities
 *
 * Utilities for handling SEO-friendly URLs for calculator with OSD and tariff params.
 *
 * URL Format: /kalkulatory/podstawowy/{osd}/{tariff}
 * - OSD: lowercase (tauron, pge, enea, energa)
 * - Tariff: uppercase (G11, G12, G12w, G12as, G13, G13s, G14, G12n, G12r)
 *
 * @see /docs/features/SEO_FRIENDLY_URLS.md
 */

import { OSDCode, OSD_CODES, getAvailableTariffsForOSD, getOSDShortName } from './osdMetadata';
import { TariffType } from '../types/graphql';

// ============================================================================
// OSD URL MAPPING
// ============================================================================

/**
 * Mapping from lowercase URL slug to OSD code
 */
export const OSD_URL_MAPPING: Record<string, OSDCode> = {
  'tauron': 'TAURON',
  'pge': 'PGE',
  'enea': 'ENEA',
  'energa': 'ENERGA',
};

/**
 * Reverse mapping from OSD code to URL slug
 */
export const OSD_CODE_TO_SLUG: Record<OSDCode, string> = {
  'TAURON': 'tauron',
  'PGE': 'pge',
  'ENEA': 'enea',
  'ENERGA': 'energa',
};

// ============================================================================
// TARIFF DISPLAY NAMES (for SEO)
// ============================================================================

/**
 * Human-readable tariff descriptions for SEO meta tags
 */
export const TARIFF_SEO_DESCRIPTIONS: Record<string, string> = {
  'G11': 'jednostrefowa (stała cena całą dobę)',
  'G12': 'dwustrefowa (dzień/noc)',
  'G12w': 'weekendowa (tańsza noc i weekendy)',
  'G12as': 'antismogowa (tańsza noc dla pomp ciepła)',
  'G13': 'trzystrefowa (szczyt/poza szczytem/noc)',
  'G13s': 'trzystrefowa antismogowa',
  'G14': 'czterostrefowa (dla prosumentów)',
  'G12n': 'nocna PGE (bardzo tania noc 01:00-05:00)',
  'G12r': 'rozszerzona weekendowa Energa',
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate and parse OSD from URL parameter
 *
 * @param osdParam - OSD parameter from URL (lowercase)
 * @returns Valid OSDCode or null if invalid
 */
export function parseOSDFromUrl(osdParam: string | undefined): OSDCode | null {
  if (!osdParam) return null;

  const normalized = osdParam.toLowerCase().trim();
  return OSD_URL_MAPPING[normalized] || null;
}

/**
 * Validate tariff for a specific OSD
 *
 * @param tariffParam - Tariff parameter from URL
 * @param osdCode - OSD code to validate against
 * @returns Valid tariff or 'G11' as fallback (auto-correction)
 */
export function parseTariffFromUrl(
  tariffParam: string | undefined,
  osdCode: OSDCode
): TariffType {
  if (!tariffParam) return TariffType.G11;

  const normalized = tariffParam.trim().toLowerCase();
  const availableTariffs = getAvailableTariffsForOSD(osdCode);

  // Find matching tariff (case-insensitive)
  const matchedTariff = availableTariffs.find(
    t => t.toLowerCase() === normalized
  );

  if (matchedTariff) {
    return matchedTariff as TariffType;
  }

  // Auto-correction: return G11 as fallback
  return TariffType.G11;
}

/**
 * Check if a tariff is valid for any OSD
 */
export function isValidTariffCode(tariffCode: string): boolean {
  const allTariffs = [
    'G11', 'G12', 'G12W', 'G12AS', 'G13', 'G13S', 'G14', 'G12N', 'G12R'
  ];
  return allTariffs.includes(tariffCode.toUpperCase());
}

// ============================================================================
// SEO METADATA GENERATORS
// ============================================================================

/**
 * Generate SEO title for calculator page
 *
 * Format examples:
 * - Base: "Kalkulator taryf energetycznych"
 * - With OSD: "Kalkulator taryf dla Tauron"
 * - With OSD+Tariff: "Kalkulator taryfy G13 dla Tauron"
 */
export function generateSeoTitle(
  osdCode: OSDCode | null,
  tariffCode: TariffType | null
): string {
  if (osdCode && tariffCode && tariffCode !== TariffType.G11) {
    const osdName = getOSDShortName(osdCode);
    return `Kalkulator taryfy ${tariffCode} dla ${osdName}`;
  }

  if (osdCode) {
    const osdName = getOSDShortName(osdCode);
    return `Kalkulator taryf dla ${osdName}`;
  }

  return 'Kalkulator taryf energetycznych';
}

/**
 * Get current year for SEO descriptions
 * Dynamic to avoid showing outdated year
 */
function getCurrentYear(): number {
  return new Date().getFullYear();
}

/**
 * Generate SEO meta description for calculator page
 *
 * Examples:
 * - Base: "Porównaj taryfy energetyczne i dowiedz się, która jest najbardziej opłacalna..."
 * - With OSD: "Porównaj taryfy energetyczne dla Tauron..."
 * - With OSD+Tariff: "Sprawdź taryfę G13 trzystrefową dla Tauron..."
 */
export function generateSeoDescription(
  osdCode: OSDCode | null,
  tariffCode: TariffType | null
): string {
  const year = getCurrentYear();

  if (osdCode && tariffCode) {
    const osdName = getOSDShortName(osdCode);
    const tariffDesc = TARIFF_SEO_DESCRIPTIONS[tariffCode] || '';
    return `Sprawdź taryfę ${tariffCode} ${tariffDesc} dla ${osdName}. Oblicz koszty energii elektrycznej i porównaj z innymi taryfami. Kalkulator taryf ${year}.`;
  }

  if (osdCode) {
    const osdName = getOSDShortName(osdCode);
    return `Porównaj wszystkie taryfy energetyczne dostępne dla ${osdName}. Sprawdź która taryfa G11, G12, G12w lub inna jest dla Ciebie najlepsza. Kalkulator ${year}.`;
  }

  return `Porównaj taryfy energetyczne i dowiedz się, która jest dla Ciebie najbardziej opłacalna. Kalkulator taryf energetycznych ${year} - G11, G12, G12w, G13 i więcej.`;
}

/**
 * Generate canonical URL for calculator page
 */
export function generateCanonicalUrl(
  baseUrl: string,
  osdCode: OSDCode | null,
  tariffCode: TariffType | null
): string {
  let path = '/kalkulatory/podstawowy';

  if (osdCode) {
    path += `/${OSD_CODE_TO_SLUG[osdCode]}`;

    if (tariffCode && tariffCode !== TariffType.G11) {
      path += `/${tariffCode}`;
    }
  }

  return `${baseUrl}${path}`;
}

// ============================================================================
// SITEMAP GENERATION HELPERS
// ============================================================================

/**
 * Get all valid OSD/Tariff combinations for sitemap
 *
 * @returns Array of { osd, tariff, url } objects
 */
export function getAllValidUrlCombinations(): Array<{
  osd: OSDCode;
  tariff: TariffType;
  url: string;
  priority: number;
}> {
  const combinations: Array<{
    osd: OSDCode;
    tariff: TariffType;
    url: string;
    priority: number;
  }> = [];

  for (const osd of OSD_CODES) {
    const availableTariffs = getAvailableTariffsForOSD(osd);
    const slug = OSD_CODE_TO_SLUG[osd];

    for (const tariff of availableTariffs) {
      combinations.push({
        osd,
        tariff,
        url: `/kalkulatory/podstawowy/${slug}/${tariff}`,
        priority: tariff === TariffType.G11 ? 0.75 : 0.7,
      });
    }
  }

  return combinations;
}

/**
 * Get OSD-only URLs for sitemap
 */
export function getOsdOnlyUrls(): Array<{
  osd: OSDCode;
  url: string;
  priority: number;
}> {
  return OSD_CODES.map(osd => ({
    osd,
    url: `/kalkulatory/podstawowy/${OSD_CODE_TO_SLUG[osd]}`,
    priority: 0.8,
  }));
}

export default {
  OSD_URL_MAPPING,
  OSD_CODE_TO_SLUG,
  TARIFF_SEO_DESCRIPTIONS,
  parseOSDFromUrl,
  parseTariffFromUrl,
  isValidTariffCode,
  generateSeoTitle,
  generateSeoDescription,
  generateCanonicalUrl,
  getAllValidUrlCombinations,
  getOsdOnlyUrls,
};
